import { useDocumentRoleOptions } from '../../../hooks/useDocumentRoleOptions'
import { DocumentVisibility } from '../../../documents.types'
import { PermissionsHeader } from './permissions-header'
import { PermissionsRoleList } from './permissions-role-list'
import { PermissionsSummary } from './permissions-summary'

// Plain controlled value/onChange rather than react-hook-form — see georeference-step.tsx.
type PermissionsStepProps = {
  visibility: DocumentVisibility
  roleIds: string[]
  onChange: (next: { visibility: DocumentVisibility; roleIds: string[] }) => void
  rolesError?: string
  // Edit only: if GET .../permissions fails, surface that rather than showing an empty,
  // misleadingly-saveable checklist.
  existingRolesUnknown?: boolean
}

export const PermissionsStep = ({
  visibility,
  roleIds,
  onChange,
  rolesError,
  existingRolesUnknown,
}: PermissionsStepProps) => {
  const roleOptions = useDocumentRoleOptions()

  const toggleRole = (roleId: string, checked: boolean) => {
    const nextRoleIds = checked ? [...roleIds, roleId] : roleIds.filter((id) => id !== roleId)
    // Unticking the last role would leave an invalid state — fall back to Private instead.
    onChange({
      visibility: nextRoleIds.length === 0 ? 'personal' : 'organisation',
      roleIds: nextRoleIds,
    })
  }

  const setPrivate = (checked: boolean) => {
    onChange(
      checked ? { visibility: 'personal', roleIds: [] } : { visibility: 'organisation', roleIds },
    )
  }

  const isPrivate = visibility === 'personal'

  return (
    <div className="flex flex-col gap-4">
      <PermissionsHeader isPrivate={isPrivate} existingRolesUnknown={existingRolesUnknown} />

      <PermissionsRoleList
        roleOptions={roleOptions}
        roleIds={roleIds}
        isPrivate={isPrivate}
        onToggleRole={toggleRole}
        onSetPrivate={setPrivate}
        rolesError={rolesError}
      />

      <PermissionsSummary roleOptions={roleOptions} roleIds={roleIds} isPrivate={isPrivate} />
    </div>
  )
}
